/**
 * 削除した内訳と見積書のゴミ箱の画面の部品（#200、docs/81）
 *   ・DeletedSheetsDialog：見積書の内訳タブの帯の「削除した内訳 n」→ 窓（名前・行数・小計・削除日時・削除者・あと n日・復元）
 *   ・TrashTable：見積書の一覧のタブ「ゴミ箱」の表（{customer}・{subject}・削除日・削除者・No.・金額・残り・復元・完全に削除）
 *     「完全に削除」は番号なしの行だけ（#265、docs/81 §12。確認の窓 → DELETE /estimates/:id/trash）。番号ありは「保存」＝復元だけ
 *   ・trashedOf／useUntrash：ゴミ箱の見積書を開いたとき（止めの画面）と復元
 *   ・useTrashFlash：削除して一覧へ移った直後の通知「ゴミ箱に移しました」＋「元に戻す」
 * 30日を過ぎたものは毎日の掃除で消える（番号のある見積書は消えない）。中身はゴミ箱の見積書が DB の行の守りで見えない＝ここで開かない
 */
import {
  SHEET_TRASH_MSG,
  TRASH_MSG,
  trashDaysLeft,
  type DeletedSheetList,
  type TrashList,
  type TrashedInfo,
  type UntrashResult,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { notify, notifyError } from "../../components/Notice.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { useLabels } from "../../labels.js";
import { fmtDateTimeShort } from "../../lib/dates.js";
import { yen } from "../../lib/num.js";

/** 「あと n日」／「まもなく削除」／番号ありは「保存」 */
export function leftText(purgeAt: string | null): string {
  const n = trashDaysLeft(purgeAt);
  if (n == null) return TRASH_MSG.kept;
  return n <= 0 ? TRASH_MSG.soon : TRASH_MSG.left.replace("{n}", String(n));
}

/** GET /estimates/:id の答えがゴミ箱の見積書（404 ESTIMATE_TRASHED）なら、削除日・削除者・番号 */
export function trashedOf(err: unknown): TrashedInfo | null {
  if (!(err instanceof ApiError) || err.code !== "ESTIMATE_TRASHED") return null;
  const t = err.problem.trashed as TrashedInfo | undefined;
  return t ?? { deletedAt: new Date().toISOString(), deletedBy: null, documentNo: null };
}

/**
 * ゴミ箱から戻す（POST /estimates/:id/untrash）。一覧・ホーム・案件を読み直し、通知「「件名」を復元しました」を出す。
 * opts.quiet＝通知を出さない（このあと別の画面へ移るとき：移ると通知は消えるので、移った先で出す＝nav の state.notice）。戻せなければ null
 */
export function useUntrash(): (id: string, opts?: { quiet?: boolean }) => Promise<UntrashResult | null> {
  const qc = useQueryClient();
  return async (id, opts = {}) => {
    try {
      const r = await api<UntrashResult>("POST", `/estimates/${id}/untrash`, {});
      if (!opts.quiet) notify({ text: r.message, testId: "untrash-notice" });
      for (const k of [["estimates"], ["estimates-trash"], ["home"], ["group-list"], ["groups"], ["estimate", id]]) void qc.invalidateQueries({ queryKey: k });
      return r;
    } catch (e) {
      notifyError(messageOf(e));
      return null;
    }
  };
}

/** 削除して一覧へ移った直後：「ゴミ箱に移しました」＋「元に戻す」（state.trashed＝{ id }。前の画面が nav の state で渡す） */
export function useTrashFlash(): void {
  const loc = useLocation();
  const nav = useNavigate();
  const untrash = useUntrash();
  const done = useRef(false);
  useEffect(() => {
    const st = loc.state as { trashed?: { id: string } } | null;
    if (done.current || !st?.trashed) return;
    done.current = true;
    const id = st.trashed.id;
    notify({
      text: TRASH_MSG.moved,
      testId: "trash-notice",
      action: { label: TRASH_MSG.undo, onClick: () => void untrash(id, { quiet: true }).then((r) => r && nav(`/estimates/${id}`, { state: { notice: r.message } })) },
    });
    const { trashed: _t, ...rest } = st;
    nav(loc.pathname + loc.search, { replace: true, state: Object.keys(rest).length ? rest : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** 見積書の一覧のタブ「ゴミ箱」の表。行を押しても開かない（中身は復元してから） */
export function TrashTable() {
  const L = useLabels();
  const qc = useQueryClient();
  const untrash = useUntrash();
  const q = useQuery({ queryKey: ["estimates-trash"], queryFn: () => api<TrashList>("GET", "/estimates/trash") });
  const items = q.data?.items ?? [];
  /** 完全に削除の確認の窓（#265。#271 から番号ありの行にも）。subject＝窓と通知に出す名前（件名が空なら{customer}）・no＝番号（欠番になると窓に書く） */
  const [ask, setAsk] = useState<{ id: string; subject: string; no: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const purge = async () => {
    if (!ask) return;
    setBusy(true);
    try {
      await api<void>("DELETE", `/estimates/${ask.id}/trash`);
      notify({ text: TRASH_MSG.purged.replace("{subject}", ask.subject), testId: "purge-notice" });
      for (const k of [["estimates-trash"], ["estimates"], ["home"], ["group-list"], ["groups"], ["group"]]) void qc.invalidateQueries({ queryKey: k });
      setAsk(null);
    } catch (e) {
      notifyError(messageOf(e));
      setAsk(null);
    } finally {
      setBusy(false);
    }
  };
  const close = () => !busy && setAsk(null);
  return (
    <>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      <table className="list trash-list" data-testid="trash-list">
        <thead>
          <tr>
            <th>{L.customer}</th>
            <th>{L.subject}</th>
            <th>{TRASH_MSG.headDeletedAt}</th>
            <th>{TRASH_MSG.headDeletedBy}</th>
            <th>No.</th>
            <th className="amt">金額（税込）</th>
            <th>{TRASH_MSG.headLeft}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.id} data-testid="trash-row">
              <td>
                <b>{x.clientName}</b>
                {x.branchName ? <span className="muted"> {x.branchName}</span> : null}
              </td>
              <td>{x.subject || <span className="muted">（{L.subject}はまだ）</span>}</td>
              <td className="num">{fmtDateTimeShort(x.deletedAt)}</td>
              <td>{x.deletedBy ?? <span className="muted">—</span>}</td>
              <td className="num">{x.documentNo ?? <span className="muted">—</span>}</td>
              <td className="amt num">{yen(x.total)} 円</td>
              <td className={x.purgeAt ? "trash-left" : "trash-left is-kept"} title={x.purgeAt ? undefined : TRASH_MSG.keptHelp} data-testid="trash-left">
                {leftText(x.purgeAt)}
              </td>
              <td className="trash-acts">
                <button type="button" className="small" onClick={() => void untrash(x.id)} aria-label={`${x.subject || x.clientName} を${TRASH_MSG.restore}`} data-testid="trash-restore">
                  {TRASH_MSG.restore}
                </button>
                <button type="button" className="btn text danger trash-purge" onClick={() => setAsk({ id: x.id, subject: x.subject || x.clientName, no: x.documentNo })} aria-label={`${x.subject || x.clientName} を${TRASH_MSG.purge}`} data-testid="trash-purge">
                  {TRASH_MSG.purge}
                </button>
              </td>
            </tr>
          ))}
          {q.data && !items.length ? (
            <tr className="emptyrows">
              <td colSpan={8}>{TRASH_MSG.empty}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
          <div className="dlg" role="alertdialog" aria-label={TRASH_MSG.purge} onKeyDown={(e) => e.key === "Escape" && close()} data-testid="purge-dialog">
            <div className="dh">{TRASH_MSG.purge}</div>
            <div className="db">
              <p style={{ margin: 0 }} data-testid="purge-ask">{ask.no ? TRASH_MSG.purgeAskNumbered.replaceAll("{no}", ask.no).replace("{subject}", ask.subject) : TRASH_MSG.purgeAsk.replace("{subject}", ask.subject)}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus disabled={busy} onClick={close}>{TRASH_MSG.purgeCancel}</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void purge()} data-testid="purge-confirm">{TRASH_MSG.purgeConfirm}</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * 削除した内訳の窓。復元は見積書の画面が行う（書きかけを送り終えてから版つきで呼び、答えで行と紙を入れ替える＝onRestore）。
 * 1枚の見積書・出した回は押せず、理由を1行
 */
export function DeletedSheetsDialog({ estimateId, busy, onRestore, onClose }: { estimateId: string; busy: boolean; onRestore: (id: string, name: string) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ["estimate-deleted-sheets", estimateId], queryFn: () => api<DeletedSheetList>("GET", `/estimates/${estimateId}/deleted-sheets`) });
  const blocked = q.data?.blockedBy ?? null;
  const items = q.data?.items ?? [];
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg dsheets-dlg" role="dialog" aria-label={SHEET_TRASH_MSG.title} data-testid="deleted-sheets" onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}>
        <div className="dh">{SHEET_TRASH_MSG.title}</div>
        <div className="db">
          {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
          {blocked ? (
            <p className="hint" style={{ margin: 0 }} data-testid="deleted-sheets-blocked">
              {blocked === "one_sheet" ? SHEET_TRASH_MSG.oneSheet : SHEET_TRASH_MSG.issued}
            </p>
          ) : null}
          {q.data && !items.length ? <p style={{ margin: 0 }}>{SHEET_TRASH_MSG.empty}</p> : null}
          {items.length ? (
            <table className="list dsheets">
              <tbody>
                {items.map((x) => (
                  <tr key={x.id} data-testid="deleted-sheet">
                    <td>
                      <b>{x.name || "（名前なし）"}</b>
                      {!x.inUse ? <span className="sheet-unused">不使用</span> : null}
                    </td>
                    <td className="num">{SHEET_TRASH_MSG.lines.replace("{n}", String(x.lineCount))}</td>
                    <td className="amt num">{yen(x.subtotal)} 円</td>
                    <td className="num muted">{fmtDateTimeShort(x.deletedAt)}</td>
                    <td className="muted">{x.deletedBy ?? "—"}</td>
                    <td className="muted" data-testid="deleted-sheet-left">{leftText(x.purgeAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button type="button" className="small ai" disabled={busy || !!blocked} onClick={() => onRestore(x.id, x.name)} data-testid="deleted-sheet-restore">
                        {SHEET_TRASH_MSG.restore}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
        <div className="df">
          <button type="button" className="btn primary" autoFocus disabled={busy} onClick={onClose}>
            {SHEET_TRASH_MSG.close}
          </button>
        </div>
      </div>
    </div>
  );
}
